/**
 * Blog article extras: a sticky table of contents on the right with scroll-spy (the heading
 * you're reading gets larger and takes the brand gradient), a compact collapsible version for
 * narrower screens, a thin reading-progress bar, and the share buttons.
 */

const HEADER_OFFSET = 150;

const content = document.querySelector('.blog-post-content');

/**
 * From 990px up the theme scrolls inside `.page-wrapper` (html/body are overflow hidden),
 * below that the window scrolls, so scroll listeners and metrics have to cover both.
 */
function getScrollMetrics() {
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper instanceof HTMLElement && getComputedStyle(wrapper).overflowY === 'auto') {
    return { top: wrapper.scrollTop, height: wrapper.clientHeight, total: wrapper.scrollHeight };
  }
  return { top: window.scrollY, height: window.innerHeight, total: document.documentElement.scrollHeight };
}

/** @param {() => void} callback */
function onScroll(callback) {
  let ticking = false;
  const handler = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      callback();
      ticking = false;
    });
  };
  window.addEventListener('scroll', handler, { passive: true });
  document.querySelector('.page-wrapper')?.addEventListener('scroll', handler, { passive: true });
  window.addEventListener('resize', handler);
}

if (content) {
  setUpShareButtons();
  setUpProgressBar(content);
  setUpTableOfContents(content);
}

/** @param {string} text */
function slugify(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/õ/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** @param {HTMLElement} root */
function setUpTableOfContents(root) {
  /** @type {HTMLHeadingElement[]} */
  const headings = Array.from(root.querySelectorAll('h2')).filter((heading) => !heading.closest('.blog-cta'));
  if (headings.length < 3) return;

  const usedIds = new Set(Array.from(document.querySelectorAll('[id]')).map((el) => el.id));
  for (const heading of headings) {
    if (heading.id) continue;
    const base = slugify(heading.textContent || '') || 'osa';
    let id = base;
    let n = 2;
    while (usedIds.has(id)) id = `${base}-${n++}`;
    heading.id = id;
    usedIds.add(id);
  }

  const buildList = () => {
    const list = document.createElement('ol');
    for (const heading of headings) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent?.trim() || '';
      link.dataset.target = heading.id;
      item.append(link);
      list.append(item);
    }
    return list;
  };

  // Right-hand rail (wide screens)
  const rail = document.createElement('aside');
  rail.className = 'blog-toc-rail';
  rail.setAttribute('aria-label', 'Sisukord');
  const nav = document.createElement('nav');
  nav.className = 'blog-toc';
  const title = document.createElement('p');
  title.className = 'blog-toc__title';
  title.textContent = 'Sisukord';
  nav.append(title, buildList());

  // Left sidebar stack: contents box, share row and author card (clones of the ones under the text,
  // which stay for narrow screens). Clicks on the cloned share buttons work via delegation.
  const stack = document.createElement('div');
  stack.className = 'blog-toc-stack';
  stack.append(nav);
  for (const selector of ['.blog-share', '.blog-author']) {
    const original = document.querySelector(selector);
    if (!original) continue;
    const clone = /** @type {HTMLElement} */ (original.cloneNode(true));
    clone.classList.remove('blog-extra');
    clone.classList.add('blog-side-block');
    stack.append(clone);
  }
  rail.append(stack);
  // Lives in the section wrapper (not the text) so it starts level with the title
  const wrapper = root.closest('.section-content-wrapper') || root;
  wrapper.append(rail);

  // The rail spans the whole article area (text, share row, related) so the contents list stays
  // pinned while the article moves, and stops with a margin before the footer.
  const syncRailHeight = () => {
    rail.style.bottom = 'auto';
    rail.style.height = `${Math.max(0, wrapper.getBoundingClientRect().height - 64)}px`;
  };
  syncRailHeight();
  new ResizeObserver(syncRailHeight).observe(wrapper);
  window.addEventListener('load', syncRailHeight);

  // Compact version inside the text (narrower screens)
  const inline = document.createElement('details');
  inline.className = 'blog-toc-inline';
  const summary = document.createElement('summary');
  summary.textContent = 'Sisukord';
  inline.append(summary, buildList());
  headings[0].before(inline);

  const EDGE = 24; // gap to the top edge when the site header is hidden
  const HEADER_GAP = 16; // gap below the header when it is showing
  const BOTTOM_GAP = 24;

  /** Where the contents list should sit: right under the header if it is visible, else at the top edge. */
  const getPinTop = () => {
    const header = document.querySelector('header-component');
    const headerBottom = header instanceof HTMLElement ? header.getBoundingClientRect().bottom : 0;
    return headerBottom > 0 ? Math.round(headerBottom + HEADER_GAP) : EDGE;
  };

  /** Keeps the contents list in view while the article scrolls, and parks it where the rail ends. */
  const placeToc = () => {
    if (getComputedStyle(rail).display === 'none') return;
    const pinTop = getPinTop();
    const viewportHeight = getScrollMetrics().height;
    const railRect = rail.getBoundingClientRect();

    // The list scrolls inside its box when the screen is too short to show everything (share buttons stay visible)
    stack.style.maxHeight = `${Math.max(240, viewportHeight - pinTop - BOTTOM_GAP)}px`;

    const stackHeight = stack.offsetHeight;
    const pinned = railRect.top < pinTop && railRect.bottom - stackHeight > pinTop;
    const resting = railRect.top < pinTop && !pinned;

    stack.classList.toggle('is-pinned', pinned);
    stack.classList.toggle('is-resting', resting);
    stack.style.left = pinned ? `${railRect.left}px` : '';
    stack.style.top = pinned ? `${pinTop}px` : '';
  };

  const links = Array.from(document.querySelectorAll('.blog-toc a, .blog-toc-inline a'));
  /** @type {string | null} */
  let activeId = null;

  const update = () => {
    let current = null;
    for (const heading of headings) {
      if (heading.getBoundingClientRect().top <= HEADER_OFFSET) current = heading.id;
    }
    // At the very bottom of the page the last heading may never reach the offset
    const { top, height, total } = getScrollMetrics();
    if (total > height && top + height >= total - 4) {
      current = headings[headings.length - 1].id;
    }
    placeToc();

    if (current === activeId) return;
    activeId = current;

    for (const link of links) {
      const isActive = link.dataset.target === activeId;
      link.classList.toggle('is-active', isActive);
      link.parentElement?.classList.toggle('is-active', isActive);
      if (isActive) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  };

  onScroll(update);
  update();
}

/** @param {HTMLElement} root */
function setUpProgressBar(root) {
  const bar = document.createElement('div');
  bar.className = 'blog-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.append(bar);

  const update = () => {
    const rect = root.getBoundingClientRect();
    const total = rect.height - window.innerHeight * 0.4;
    const progress = total > 0 ? Math.min(1, Math.max(0, (window.innerHeight * 0.3 - rect.top) / total)) : 0;
    bar.style.transform = `scaleX(${progress})`;
  };

  onScroll(update);
  update();
}

/** @type {HTMLElement | null} */
let toast = null;
/** @type {number | undefined} */
let toastTimer;

/** Small white notice at the bottom center of the screen, in the style of Shopify's Polaris toasts. */
function showToast(/** @type {string} */ message) {
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'blog-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.append(toast);
  }

  toast.textContent = message;
  // Restart the transition so repeated clicks re-show it
  toast.classList.remove('is-visible');
  void toast.offsetWidth;
  toast.classList.add('is-visible');

  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast?.classList.remove('is-visible'), 2500);
}

function setUpShareButtons() {
  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-blog-share="copy"]');
    if (!(button instanceof HTMLElement)) return;

    const url = button.dataset.url || window.location.href;
    const label = button.querySelector('[data-share-label]');
    try {
      await navigator.clipboard.writeText(url);
      button.dataset.state = 'copied';
      button.title = 'Link kopeeritud';
      if (label) label.textContent = 'Link kopeeritud';
      showToast('Link kopeeritud');
    } catch {
      button.title = 'Kopeeri link käsitsi';
      showToast('Kopeeri link käsitsi');
    }
    setTimeout(() => {
      delete button.dataset.state;
      button.title = 'Kopeeri link';
      if (label) label.textContent = 'Kopeeri link';
    }, 2000);
  });
}
